/**
 * ActivityThreadCard — the Activity layout's thread row.
 *
 * Three lines, ported from t3code's sidebar card:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ▣ project-name                      Working  │  ← identity + state
 *   │ Fix the relay handshake                      │  ← what it is
 *   │ ⑂ feat/relay  ⌨  #482  2 agents              │  ← where it lives
 *   └──────────────────────────────────────────────┘
 *
 * The metrics are t3code's verbatim, because "roughly the same shape" is what
 * made the first cut read as an imitation: 78px tall, 20px first line, 16px
 * favicon, and type at 12 · 14 · 12.
 *
 * **Why the sizes are `calc(var(--acard) * N)` and not Tailwind's rem scale.**
 * `--acard` is `1rem`, and every `N` below is the reference design's own rem
 * value, so the card is dimensionally identical to the drawing it came from.
 *
 * It is a custom property rather than `em` because `em` compounds: the status
 * slot sets its own `text-…*0.75`, so an `em` height there would resolve
 * against 12px and come out 15px instead of the intended 20px. `--acard` is
 * fixed at the card level, so no measurement depends on where it sits.
 *
 * It was previously `--forge-ui-font-size × 8/7`, a bridge that reconstructed
 * 16px from the app's old 14px root. The root is 16px now, so the bridge is
 * gone — but leaving the multiplication in place would have quietly inflated
 * every measurement on this card to 18.3px-based.
 *
 * **Every `text-[length:…]` here is paired with a `leading-[…]`.** t3code's
 * lines are `text-xs`, `text-sm`, `text-xs`, and those utilities set a
 * line-height as well as a font-size — 16px, 20px, 16px. The arbitrary form
 * sets font-size alone, so the leading falls through to preflight's 1.5 ratio
 * and the lower two lines render at 21px and 18px. The card is 78px of fixed
 * height over an `overflow-hidden` surface, so that 3px surplus does not push
 * the card taller; it clips the branch line from underneath, which is why the
 * measurement to trust is `scrollHeight === clientHeight`, not `height`.
 *
 * The surfaces and tokens are t3code's, taken from Forge where it already has
 * them: `--sidebar-row-content-inset`, `--sidebar-content-inset`, and
 * `sidebar-row-hover|active|selected` were already defined here with the same
 * dark values. `--secondary-label` was not, so it is ported into `index.css`
 * rather than substituted — the project label, branch line, and receded titles
 * all read from it, exactly as they do there.
 *
 * The surface model itself (hover, active, selected, in-flight fade) lives in
 * `activityThreadCard.logic.ts` as `resolveActivityRowSurfaceClassName`, shared
 * with `ActivityArchivedRow`: t3code draws both arrangements on one surface, and
 * two components that differ only in layout must not also differ in what a
 * hover looks like.
 *
 * The compact `SidebarThreadRow` stays exactly as it was and still serves the
 * Projects layout. The two are deliberately separate components rather than one
 * component with a `variant` prop: they answer different questions. A row under
 * a project already knows its project and can spend its whole width on the
 * title; a card in one flat list has to carry its own context, which is the
 * entire reason the Activity layout can drop the project tree.
 *
 * Behaviour is not forked. Both render through `useSidebarThreadRow`, so
 * selection, range-select, the context menu, rename, and PR opening are the
 * same code on both layouts. Archive can confirm inline; Settle never confirms.
 */

import { BotIcon, PinIcon, TerminalIcon } from "lucide-react";

import { ProjectIcon } from "../ProjectIcon";
import { PROVIDER_SETTINGS_ICON_BY_PROVIDER } from "../providerSettingsIcons";
import { ThreadOriginIcon } from "./ThreadOriginIcon";
import { selectEnvironmentState, useStore } from "../../store";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";
import { SidebarMenuSubItem } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { cn } from "~/lib/utils";
import { useSidebarThreadRow } from "./useSidebarThreadRow";
import {
  type ActivityCardEmphasisModel,
  compactActivityCardTimeLabel,
  resolveActivityCardBranchLabel,
  resolveActivityCardEmphasis,
  resolveActivityCardPrBadge,
  resolveActivityCardTimestamp,
  resolveActivityRowSurfaceClassName,
  resolveActivityCardProvider,
} from "./activityThreadCard.logic";
import { ActivityCardWorktreeGlyph } from "./ActivityCardWorktreeGlyph";
import { ActivityThreadCardTooltip } from "./ActivityThreadCardTooltip";
import { resolveLocalCheckoutBranchMismatch } from "../BranchToolbar.logic";
import { ActivityCardStatusSlot } from "./ActivityCardStatusSlot";
import { resolveActivityCardArchiveControl } from "./activityCardArchiveControl";
import { SidebarThreadAgentSummary, SidebarThreadWorkerToggle } from "./SidebarThreadWorkerSummary";
import { resolveSidebarThreadIdentity, type SidebarThreadRowProps } from "./SidebarThreadRow";
import { SidebarThreadJumpHint, SidebarThreadWorkItemBadge } from "./SidebarThreadRowBadges";
import { useTaskWorkItemKey } from "./TaskWorkItemKeyContext";
type ActivityThreadCardState = ReturnType<typeof useSidebarThreadRow>;

export interface ActivityThreadCardProps extends SidebarThreadRowProps {
  /** Names the project on the card's first line, and in the board link's label. */
  readonly projectName: string | null;
  /**
   * The project's CMMD-owned icon, when it has one. Supplied from the same
   * index the picker resolves from, so the card and the dropdown above it draw
   * one project one way.
   */
  readonly projectIcon?: { readonly iconUrl: string | null; readonly icon: string | null };
}

export interface ActivityCardContext {
  readonly cardProps: ActivityThreadCardProps;
  readonly state: ActivityThreadCardState;
  readonly emphasis: ActivityCardEmphasisModel;
  /** The thread's age, shown when it has no status worth naming. */
  readonly timeLabel: string;
}

// ---------------------------------------------------------------------------
// Line 1 — project identity, pin, and the status/action slot
// ---------------------------------------------------------------------------

function ActivityCardPinGlyph(props: { readonly context: ActivityCardContext }) {
  const { cardProps, state } = props.context;
  if (cardProps.isPinned !== true) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-thread-selection-safe
            data-testid={`thread-pin-${cardProps.thread.id}`}
            aria-label={`Unpin ${cardProps.thread.title}`}
            className="inline-flex shrink-0 cursor-pointer items-center rounded-sm text-muted-foreground/65 outline-none transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
            onPointerDown={state.stopPropagationOnPointerDown}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              cardProps.onTogglePinned?.(state.threadKey);
            }}
          >
            <PinIcon aria-hidden className="size-[calc(var(--acard)*0.75)] shrink-0" />
          </button>
        }
      />
      <TooltipPopup side="top">Unpin thread</TooltipPopup>
    </Tooltip>
  );
}

/**
 * The card's project mark.
 *
 * Its own component so the hook that resolves a linked project's identity is
 * not added to the line's layout. The card's `projectIcon` is only ever set for
 * cloud rows; a local checkout linked to a CMMD project resolves through the
 * link, or the card shows the checkout's favicon over the chosen emoji.
 */
function ActivityCardProjectMark(props: { readonly context: ActivityCardContext }) {
  const { cardProps } = props.context;
  const resolveProjectIcon = useProjectIconResolver();
  return (
    <ProjectIcon
      environmentId={cardProps.thread.environmentId}
      cwd={cardProps.projectCwd ?? ""}
      projectName={cardProps.projectName}
      canonicalIdentity={
        cardProps.projectIcon ??
        resolveProjectIcon(cardProps.thread.environmentId, cardProps.thread.projectId)
      }
      className="size-[var(--acard)] shrink-0"
    />
  );
}

function ActivityCardProjectLine(props: { readonly context: ActivityCardContext }) {
  const { cardProps, emphasis } = props.context;
  return (
    <div className="flex h-[calc(var(--acard)*1.25)] min-w-0 items-center gap-[calc(var(--acard)*0.375)]">
      {/* Names the project, and no longer links anywhere: the execution
          project page this used to open is gone, and a Forge project has no
          board of its own to send the card to. */}
      {/* `gap-[calc(var(--acard)*0.375)]`, not a literal `gap-1.5`: the card
          declares `--acard` as t3code's 16px, and Tailwind's `1.5` resolves
          against Forge's 14px root instead — 5.25px where upstream draws 6px.
          Inherited from the project link this line replaced. */}
      <div
        data-testid={`thread-project-row-${cardProps.thread.id}`}
        className="flex min-w-0 flex-1 items-center gap-[calc(var(--acard)*0.375)]"
      >
        <ActivityCardProjectMark context={props.context} />
        <span
          data-testid={`thread-project-name-${cardProps.thread.id}`}
          className={cn(
            "min-w-0 flex-1 truncate text-[length:calc(var(--acard)*0.75)] leading-[var(--acard)] text-secondary-label",
            emphasis.shouldRecede ? "font-normal" : "font-medium",
          )}
        >
          {cardProps.projectName ?? "Unknown project"}
        </span>
      </div>
      <ActivityCardPinGlyph context={props.context} />
      <ActivityCardStatusSlot
        thread={cardProps.thread}
        threadStatus={props.context.state.threadStatus}
        isActive={cardProps.isActive}
        timeLabel={props.context.timeLabel}
        archiveControl={resolveActivityCardArchiveControl(props.context)}
        onSettlePointerDown={props.context.state.stopPropagationOnPointerDown}
        onSettle={props.context.state.handleSettleClick}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line 2 — the title
// ---------------------------------------------------------------------------

/** t3code's card-variant title ramp: read rows recede, unread ones do not. */
function resolveTitleToneClass(input: {
  readonly emphasis: ActivityCardEmphasisModel;
  readonly isFailed: boolean;
}): string {
  if (input.emphasis.isUnread) return "text-foreground";
  if (input.emphasis.shouldRecede) return "text-secondary-label";
  return input.isFailed ? "text-foreground/95" : "text-foreground/90";
}

function ActivityCardTitleLine(props: { readonly context: ActivityCardContext }) {
  const { cardProps, state, emphasis } = props.context;
  const identity = resolveSidebarThreadIdentity(cardProps.thread);

  if (state.renamingThreadKey === state.threadKey) {
    return (
      <div className="mt-[calc(var(--acard)*0.25)] flex min-w-0">
        <input
          ref={state.handleRenameInputRef}
          // Height, not just leading. The input carries a 1px border, and a
          // 20px line box inside it makes the control 22px — two more than the
          // title it replaces, in a card whose height is fixed. Upstream has
          // the same border on the same fixed card, so renaming there clips
          // 2px off the branch line; `h-…` is the one deliberate divergence,
          // because reproducing a clipped line is not what porting the metrics
          // was for. `box-sizing: border-box` folds the border into the 20px.
          className="h-[calc(var(--acard)*1.25)] min-w-0 flex-1 rounded-sm border border-input bg-card px-[calc(var(--acard)*0.25)] text-[length:calc(var(--acard)*0.875)] leading-[calc(var(--acard)*1.25)] font-medium text-card-foreground outline-none focus:border-foreground"
          value={cardProps.renamingTitle}
          onChange={state.handleRenameInputChange}
          onKeyDown={state.handleRenameInputKeyDown}
          onBlur={state.handleRenameInputBlur}
          onClick={state.handleRenameInputClick}
        />
      </div>
    );
  }

  return (
    <div className="mt-[calc(var(--acard)*0.25)] flex min-w-0 items-center gap-[calc(var(--acard)*0.375)]">
      {identity.agentName !== null ? (
        <BotIcon
          className="size-[calc(var(--acard)*0.75)] shrink-0 text-primary"
          aria-label={`${identity.agentName}, AI task owner`}
        />
      ) : null}
      {/* No tooltip of its own: the whole card is the trigger for the details
          panel, which shows this title untruncated along with everything else
          the card had to shorten. A second trigger nested inside the first
          would only compete for the same hover. */}
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[length:calc(var(--acard)*0.875)] leading-[calc(var(--acard)*1.25)] transition-opacity motion-reduce:transition-none",
          emphasis.shouldRecede ? "font-normal" : "font-medium",
          resolveTitleToneClass({
            emphasis,
            isFailed: state.threadStatus?.label === "Failed",
          }),
        )}
        data-testid={`thread-title-${cardProps.thread.id}`}
      >
        {identity.title}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line 3 — branch, terminal, PR, agents
// ---------------------------------------------------------------------------

function ActivityCardPrBadge(props: { readonly context: ActivityCardContext }) {
  const { cardProps, state } = props.context;
  const badge = resolveActivityCardPrBadge(state.pr);
  if (badge === null) return null;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-thread-selection-safe
            data-testid={`thread-pr-${cardProps.thread.id}`}
            aria-label={badge.tooltip}
            className={cn(
              "shrink-0 cursor-pointer tabular-nums hover:underline focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
              badge.colorClass,
            )}
            onPointerDown={state.stopPropagationOnPointerDown}
            onClick={state.handlePrClick}
          >
            {badge.label}
          </button>
        }
      />
      <TooltipPopup side="top">{badge.tooltip}</TooltipPopup>
    </Tooltip>
  );
}

function ActivityCardTerminalGlyph(props: { readonly state: ActivityThreadCardState }) {
  const terminalStatus = props.state.terminalStatus;
  if (!terminalStatus) return null;
  return (
    <span
      role="img"
      aria-label={terminalStatus.label}
      className={cn("inline-flex shrink-0 items-center", terminalStatus.colorClass)}
    >
      <TerminalIcon
        className={cn(
          "size-[calc(var(--acard)*0.875)]",
          // The stepped duty cycle, not Tailwind's continuous ease-in-out
          // `animate-pulse` — see the keyframe comment in `index.css`.
          // `motion-safe:` per DESIGN.md → "Reduced motion is a first-class
          // state". The glyph keeps its colour and its label when the motion is
          // off, so the fact survives without the animation.
          terminalStatus.pulse && "motion-safe:animate-status-pulse",
        )}
      />
    </span>
  );
}

/**
 * The meta line's right-hand identity group — t3code's `ml-auto` cluster.
 *
 * Two marks, in upstream's order: a server glyph when the thread runs somewhere
 * other than this device, then the mark of the agent driving it. Both are
 * `aria-hidden` and `pointer-events-none` there and here: they report, they are
 * not controls, and the details panel already names both in words for anyone
 * who cannot see them.
 *
 * **Where the provider comes from.** `session.provider` first — that is the
 * driver actually attached to a running thread — falling back to the thread
 * shell's `modelSelection.provider` for a thread that has never started. The
 * fallback is not an edge case: it is the freshly-created "New thread" card,
 * which is exactly where the mark was missing and where upstream still shows
 * one, because a thread's agent is chosen before its first turn.
 *
 * **Adapted, not ported verbatim.** Upstream badges *provider instances* (two
 * Claude accounts, each with an accent colour); Forge models providers, so
 * there is no instance to badge and `showInstanceBadge` has no analogue — the
 * same reduction `ActivityThreadCardTooltip` already documents for its model
 * row. The glyph keeps upstream's `opacity-60`, which is what stops a row of
 * brand marks from out-shouting the branch beside them.
 */
function ActivityCardIdentityGroup(props: { readonly context: ActivityCardContext }) {
  const { cardProps } = props.context;
  const { thread } = cardProps;
  const shellProvider = useStore(
    (state) =>
      selectEnvironmentState(state, thread.environmentId).threadShellById[thread.id]?.modelSelection
        .provider ?? null,
  );
  // Resolution order lives in the logic module so it can be tested as data and
  // so this component stays layout. Forge Cloud rows resolve to null there:
  // the CMMD inventory payload carries a model slug and no provider kind.
  const provider = resolveActivityCardProvider({
    sessionProvider: thread.session?.provider,
    shellProvider,
    durableProvider: thread.modelSelection?.provider,
  });
  const ProviderGlyph =
    provider === null ? undefined : PROVIDER_SETTINGS_ICON_BY_PROVIDER[provider];

  // `current-device` renders no origin glyph, so a card with no agent either
  // has nothing to show and drops the slot rather than leaving an empty one.
  const origin = cardProps.origin ?? "current-device";
  if (origin === "current-device" && ProviderGlyph === undefined) return null;

  return (
    <span
      aria-hidden
      data-testid={`thread-identity-${thread.id}`}
      className="pointer-events-none ml-auto inline-flex shrink-0 items-center gap-[calc(var(--acard)*0.25)]"
    >
      <ThreadOriginIcon origin={origin} className="size-[calc(var(--acard)*0.875)]" />
      {ProviderGlyph ? (
        <ProviderGlyph
          aria-hidden
          data-testid={`thread-provider-${thread.id}`}
          className="size-[calc(var(--acard)*0.875)] shrink-0 opacity-60"
        />
      ) : null}
    </span>
  );
}

function ActivityCardMetaLine(props: {
  readonly context: ActivityCardContext;
  readonly workItemKey: string | null;
  readonly workerCount: number;
}) {
  const { cardProps, state } = props.context;
  // `state.displayBranch`, not `thread.branch`: the row shows where the work is
  // now, not where it started. Same value the hover panel receives, so the two
  // can never name different branches for one thread.
  const branchLabel = resolveActivityCardBranchLabel({
    branch: state.displayBranch,
    worktreePath: cardProps.thread.worktreePath,
  });

  return (
    <div className="mt-[calc(var(--acard)*0.125)] flex min-w-0 items-center gap-[calc(var(--acard)*0.375)] text-[length:calc(var(--acard)*0.75)] leading-[var(--acard)] text-secondary-label">
      {branchLabel === null ? (
        <span className="flex-1" />
      ) : (
        <>
          <ActivityCardWorktreeGlyph
            threadId={cardProps.thread.id}
            branch={branchLabel}
            worktreePath={cardProps.thread.worktreePath}
          />
          {/* The name itself is untooltipped — the details panel carries the
              full branch, and a second trigger here would compete with the
              card's own hover. The glyph beside it is the exception: it says
              something the panel's branch row does not. */}
          <span className="min-w-0 flex-1 truncate whitespace-nowrap">{branchLabel}</span>
        </>
      )}
      <SidebarThreadWorkItemBadge workItemKey={props.workItemKey} />
      <ActivityCardTerminalGlyph state={state} />
      <ActivityCardPrBadge context={props.context} />
      {/* Last, and `ml-auto`, so it pins to the line's right edge however much
          the branch beside it truncates — upstream's arrangement exactly. */}
      <ActivityCardIdentityGroup context={props.context} />
      {/* Nothing on this card confirms before acting anymore — Archive moved
          to the context menu, and Settle never confirms — so these no longer
          need to make way for a confirmation the card can't show. */}
      <SidebarThreadAgentSummary
        workerCount={props.workerCount}
        attentionCount={cardProps.attentionCount ?? 0}
      />
      <SidebarThreadWorkerToggle
        threadKey={state.threadKey}
        threadId={cardProps.thread.id}
        workerCount={props.workerCount}
        workersExpanded={cardProps.workersExpanded ?? false}
        reserveArchiveSpace={false}
        onToggleWorkers={cardProps.onToggleWorkers}
        onPointerDown={state.stopPropagationOnPointerDown}
      />
      {cardProps.environmentBadge}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityThreadCard
// ---------------------------------------------------------------------------

export function ActivityThreadCard(props: ActivityThreadCardProps) {
  const state = useSidebarThreadRow(props);
  const resolvedWorkItemKey = useTaskWorkItemKey(props.thread.anchorCmmdId);
  const workItemKey =
    props.thread.kind === "master" && props.thread.anchorKind === "task"
      ? resolvedWorkItemKey
      : null;
  const workerCount = props.thread.kind === "master" ? (props.workerCount ?? 0) : 0;
  const timeLabel = compactActivityCardTimeLabel(
    formatRelativeTimeLabel(resolveActivityCardTimestamp(props.thread)),
  );
  const emphasis = resolveActivityCardEmphasis({
    threadStatus: state.threadStatus,
    isActive: props.isActive,
    isSelected: state.isSelected,
  });
  const context: ActivityCardContext = { cardProps: props, state, emphasis, timeLabel };
  // A worktree thread owns its checkout, so only a thread working directly in
  // the project directory can drift from the branch it claims.
  const branchMismatch = resolveLocalCheckoutBranchMismatch({
    effectiveEnvMode: props.thread.worktreePath === null ? "local" : "worktree",
    activeWorktreePath: props.thread.worktreePath,
    activeThreadBranch: props.thread.branch,
    currentGitBranch: state.currentGitBranch,
  });

  return (
    <SidebarMenuSubItem
      // `--acard` is declared here rather than on the content block so the
      // li's own gap to its neighbours is on the same scale as everything
      // inside it — t3code's `py-0.5` is 2px against its 16px root, and
      // Forge's literal `py-0.5` would be 1.75px against its 14px one.
      // `content-visibility` with the card's own height as the intrinsic size:
      // a long Activity list skips layout and paint for the cards scrolled out
      // of view, and the placeholder is the right height so the scrollbar does
      // not jump as they materialise. 96px is upstream's hint for the same card.
      className="w-full list-none [--acard:1rem] py-[calc(var(--acard)*0.125)] [content-visibility:auto] [contain-intrinsic-size:auto_96px]"
      data-thread-item
      onMouseLeave={state.handleMouseLeave}
      onBlurCapture={state.handleBlurCapture}
    >
      {/* The card IS the trigger. Every handler stays on the rendered element
          rather than on a wrapper, so Base UI merges its hover/focus props onto
          the same node that already owns click, keyboard, and context-menu —
          the surface does not gain a layer and behaviour does not move. */}
      <Tooltip>
        <TooltipTrigger
          render={
            <div
              role="button"
              tabIndex={0}
              data-testid={`thread-row-${props.thread.id}`}
              data-activity-thread-card
              className={resolveActivityRowSurfaceClassName({
                isActive: props.isActive,
                isSelected: state.isSelected,
                emphasis,
              })}
              onClick={state.handleRowClick}
              onKeyDown={state.handleRowKeyDown}
              onContextMenu={state.handleRowContextMenu}
            />
          }
        >
          {/* 8 + 20 + 4 + 20 + 2 + 16 + 8 = 78. Every line below therefore has
              to carry its line-height as well as its font-size:
              `text-[length:…]` sets only the latter, and the 1.5 ratio the rest
              would inherit turns the two lower lines into 21 and 18,
              overflowing this box. */}
          <div className="relative z-10 h-[calc(var(--acard)*4.875)] px-[calc(var(--acard)*0.625)] py-[calc(var(--acard)*0.5)] text-[length:var(--acard)]">
            <ActivityCardProjectLine context={context} />
            <ActivityCardTitleLine context={context} />
            <ActivityCardMetaLine
              context={context}
              workItemKey={workItemKey}
              workerCount={workerCount}
            />
          </div>
          {/* Last child of the card surface, which is the `relative` box the
              pill positions against. Outside it the hint would anchor to the
              list instead of the row. */}
          <SidebarThreadJumpHint label={props.jumpLabel} threadId={props.thread.id} />
        </TooltipTrigger>
        <ActivityThreadCardTooltip
          thread={props.thread}
          displayBranch={state.displayBranch}
          projectTitle={props.projectName}
          projectCwd={props.projectCwd}
          environmentLabel={props.environmentLabel ?? null}
          branchMismatch={branchMismatch}
          terminalStatus={state.terminalStatus}
          terminalProcessCount={state.runningTerminalCount}
        />
      </Tooltip>
    </SidebarMenuSubItem>
  );
}
